import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AudioWaveform,
  Upload,
  Music2,
  Clock,
  Trash2,
  MoreVertical,
  FileAudio,
  MessageSquare,
  LogOut,
  User,
  Plus,
  Loader2,
  ChevronRight,
  Zap,
  ListMusic,
  Library,
  Mic,
  Film,
  BookOpen,
  Youtube,
  Link2,
  SlidersHorizontal
} from "lucide-react";
import { toast } from "sonner";
import { trackProductEvent } from "@/lib/productMetrics";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [analyses, setAnalyses] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchAnalyses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/analysis/`, {
        headers: getAuthHeaders()
      });
      setAnalyses(response.data);
    } catch (error) {
      console.error("Error fetching analyses:", error);
      toast.error("Error al cargar los análisis");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  const fetchUsageSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/usage`, {
        headers: getAuthHeaders()
      });
      setUsageSummary(response.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsageSummary();
  }, [fetchUsageSummary]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["audio/mpeg", "audio/wav", "audio/flac", "audio/ogg", "audio/m4a", "audio/aac", "audio/mp3"];
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (!allowedTypes.some(t => file.type === t) && !["mp3", "wav", "flac", "ogg", "m4a", "aac"].includes(extension)) {
      toast.error("Formato no soportado. Usa MP3, WAV, FLAC, OGG o M4A");
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 100MB");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API_URL}/analysis/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });

      toast.success("¡Análisis completado!");
      await fetchUsageSummary();
      navigate(`/analysis/${response.data.id}`);
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error(error.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/analysis/${id}`, {
        headers: getAuthHeaders()
      });
      setAnalyses(analyses.filter(a => a.id !== id));
      toast.success("Análisis eliminado");
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  };

  const quotaItems = usageSummary ? [
    { label: "Análisis", key: "analysis", used: usageSummary.usage.analyses_used },
    { label: "Stems", key: "stems", used: usageSummary.usage.stems_used },
    {
      label: usageSummary.plan === "pro" ? "Assistant" : "Assistant hoy",
      key: "assistant",
      used: usageSummary.plan === "pro" ? usageSummary.usage.assistant_used : usageSummary.assistant_daily.used
    },
    { label: "Media import", key: "media_import", used: usageSummary.usage.media_import_used },
  ] : [];

  return (
    <div className="steel-page">
      {/* Main Content */}
      <main className="min-h-screen min-w-0">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 md:mb-8 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl" data-testid="dashboard-title">
              Mis Análisis
            </h1>
            <p className="text-muted-foreground mt-1">Sube un archivo de audio para analizar</p>
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.flac,.ogg,.m4a,.aac"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="min-h-11 w-full bg-primary hover:bg-primary/90 btn-glow gap-2 sm:w-auto"
              data-testid="upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Subir audio
            </Button>
          </div>
        </div>

        <section aria-label="Herramientas de audio" className="mb-6 border-y border-border bg-panel/40 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded bg-tune/10 text-tune"><SlidersHorizontal className="h-5 w-5" /></span>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Cambiar tono y BPM <span className="ml-1 text-xs text-tune">Gratis</span></h2>
                <p className="text-xs text-muted-foreground">Ajusta un audio y exporta el resultado.</p>
              </div>
            </div>
            <Link to="/pitch-tempo" className="inline-flex min-h-11 items-center gap-2 rounded border border-tune/50 px-4 text-sm font-semibold text-tune transition-colors hover:bg-tune/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tune" data-testid="pitch-tempo-entry">
              Abrir herramienta <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {usageSummary && (
          <Card className="mb-6 border-border bg-panel steel-studio-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-foreground font-semibold">
                    Uso del plan {usageSummary.plan === "pro" ? "Pro" : "Free"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Periodo {usageSummary.period_month}. Prueba el valor real y sube a Pro cuando necesites más canciones.
                  </p>
                </div>
                {usageSummary.plan !== "pro" && (
                  <Link
                    to="/pricing"
                    onClick={() => trackProductEvent("pro_cta_clicked", { location: "dashboard_usage_card" }, getAuthHeaders)}
                  >
                    <Button className="min-h-11 w-full bg-primary hover:bg-primary/90 btn-glow sm:w-auto">
                      Upgrade a PRO
                    </Button>
                  </Link>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {quotaItems.map((item) => {
                  const limit = usageSummary.limits[item.key] || 0;
                  const remaining = usageSummary.remaining[item.key] || 0;
                  const percent = limit > 0 ? Math.min(100, Math.round((item.used / limit) * 100)) : 100;
                  return (
                    <div key={item.key} className="rounded-2xl border border-border bg-ink/50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-foreground/80">{item.label}</span>
                        <span className="text-xs text-muted-foreground">{item.used}/{limit}</span>
                      </div>
                      <Progress value={percent} className="h-1.5 mb-2" />
                      <p className="text-xs text-muted-foreground">{remaining} restantes</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Card className="mb-6 border-border bg-panel steel-studio-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">Analizando audio...</p>
                  <Progress value={uploadProgress} className="mt-2 h-2" />
                </div>
                <span className="text-sm font-mono text-primary">{uploadProgress}%</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analyses Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : analyses.length === 0 ? (
          <Card className="border-border border-dashed bg-panel steel-studio-card">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FileAudio className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No hay análisis todavía
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Sube tu primer archivo de audio para ver su análisis completo
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-primary hover:bg-primary/90 gap-2"
              >
                <Plus className="w-4 h-4" />
                Subir mi primer audio
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {analyses.map((analysis) => (
              <Card
                key={analysis.id}
                className="cursor-pointer border-border bg-panel card-hover group steel-studio-card"
                onClick={() => navigate(`/analysis/${analysis.id}`)}
                data-testid={`analysis-card-${analysis.id}`}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center flex-shrink-0">
                      <Music2 className="w-7 h-7 text-primary" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                        {analysis.filename}
                      </h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="text-sm font-mono text-secondary">{analysis.key}</span>
                        <span className="text-sm font-mono text-muted-foreground">{analysis.bpm} BPM</span>
                        <span className="text-sm text-muted-foreground hidden sm:inline">
                          {formatDuration(analysis.duration)}
                        </span>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                      <span className="text-xs text-muted-foreground hidden md:inline">
                        {formatDate(analysis.created_at)}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-panel border-border">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(analysis.id);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pro Upgrade Banner */}
        <Card className="mt-8 border-primary/20 bg-gradient-to-r from-primary/10 to-accent/10 steel-studio-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Desbloquea SteelMora Pro</h3>
                  <p className="text-sm text-muted-foreground">Análisis ilimitados, separación de stems y exportación avanzada</p>
                </div>
              </div>
              <Link
                to="/pricing"
                onClick={() => trackProductEvent("pro_cta_clicked", { location: "dashboard_upgrade_banner" }, getAuthHeaders)}
              >
                <Button className="min-h-11 w-full bg-primary hover:bg-primary/90 btn-glow sm:w-auto">
                  Ver planes
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DashboardPage;
